import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Facebook, 
  Instagram, 
  Youtube, 
  Mail, 
  Phone, 
  MapPin, 
  ExternalLink, 
  Code, 
  Laptop, 
  Braces, 
  Palette, 
  Database, 
  Zap,
  Mic 
} from 'lucide-react';

const Footer = () => {
  const location = useLocation();
  const isCMSPage = location.pathname === '/cms';

  return (
    <footer className={`bg-card/95 backdrop-blur-sm border-t border-border/10 ${
      isCMSPage ? 'md:block hidden' : ''
    }`}>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* About Section */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <div className="flex items-center gap-2 group">
                <motion.div className="relative text-xl font-bold">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                    SIN
                  </span>
                  <motion.div
                    className="inline-block relative"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <Mic className="w-6 h-6 inline text-primary animate-pulse" />
                  </motion.div>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                    PSE
                  </span>
                </motion.div>
              </div>
            </Link>
            <p className="text-sm text-foreground/80 mb-4">
              Portal de Podcasts e Notícias do Agrupamento de Escolas de Albergaria a Velha, dedicado à divulgação de conteúdo educativo e informativo.
            </p>
            <div className="flex space-x-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-primary transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-primary transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-primary transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Links Rápidos</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/project" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  Projeto Sinopse
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  Nossa Equipa
                </Link>
              </li>
              <li>
                <Link to="/podcasts" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  Podcasts
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  Notícias
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  Cursos Técnico Profissionais
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  Contacto
                </Link>
              </li>
              <li>
                <Link to="/cms" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  CMS
                </Link>
              </li>
            </ul>
          </div>

          {/* Courses */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Cursos Profissionais</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/courses" className="text-sm text-foreground/60 hover:text-primary transition-colors flex items-center">
                  Técnico Multimédia
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-foreground/60 hover:text-primary transition-colors flex items-center">
                  Técnico de Gestão e Programação
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-foreground/60 hover:text-primary transition-colors flex items-center">
                  Técnico de Manutenção Industrial
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-foreground/60 hover:text-primary transition-colors flex items-center">
                  Técnico Comercial
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-foreground/60 hover:text-primary transition-colors flex items-center">
                  Técnico Auxiliar de Saúde
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Link>
              </li>
              <li>
                <Link to="/courses" className="text-sm text-foreground/60 hover:text-primary transition-colors flex items-center">
                  Técnico de Recursos Florestais
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contactos</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm text-foreground/60">
                  Rua Américo Martins Pereira, 3850-837 Albergaria-a-Velha
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary shrink-0" />
                <a href="tel:+351234529680" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  234 529 680
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary shrink-0" />
                <a href="mailto:sinopse@sinopseaeaav.pt" className="text-sm text-foreground/60 hover:text-primary transition-colors">
                  sinopse@sinopseaeaav.pt
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-sm text-foreground/60">
            © {new Date().getFullYear()} SINOPSE - João Martins e Luís Martins - Curso Multimédia AEAAV. Todos os direitos reservados.
          </p>
          <div className="flex space-x-6">
            <a href="#" className="text-sm text-foreground/60 hover:text-primary transition-colors">
              Política de Privacidade
            </a>
            <a href="#" className="text-sm text-foreground/60 hover:text-primary transition-colors">
              Termos de Uso
            </a>
          </div>
        </div>

        {/* Tech Stack Section */}
        <div className="mt-8 pt-8 border-t border-border/10">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center">
            <span className="text-sm font-medium bg-gradient-to-r from-primary to-primary/60 text-transparent bg-clip-text">
              Desenvolvido com:
            </span>
            <div className="flex items-center gap-6">
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="group relative"
                title="React"
              >
                <Laptop className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors cursor-help" />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-background rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg border border-border/10">
                  React
                </span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="group relative"
                title="TypeScript"
              >
                <Braces className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors cursor-help" />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-background rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg border border-border/10">
                  TypeScript
                </span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="group relative"
                title="Vite"
              >
                <Zap className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors cursor-help" />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-background rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg border border-border/10">
                  Vite
                </span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="group relative"
                title="Tailwind"
              >
                <Palette className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors cursor-help" />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-background rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg border border-border/10">
                  Tailwind
                </span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="group relative"
                title="Framer Motion"
              >
                <Code className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors cursor-help" />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-background rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg border border-border/10">
                  Framer
                </span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="group relative"
                title="Supabase"
              >
                <Database className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors cursor-help" />
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-background rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg border border-border/10">
                  Supabase
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;